import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Flask, PaperPlaneTilt, Plus, Printer, DownloadSimple } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';

/**
 * Claims page header — title, count, bulk-action CTAs, "New Claim" button.
 * Bulk-action buttons render only when at least one selectable claim is checked.
 *
 * P34 (Feb 27, 2026): added Rhinobill-parity Print + Download CSV buttons.
 */
const ClaimsHeader = ({
  totalCount,
  draftCount,
  selectedCount,
  onBatchSubmit,
  onOpenOdmDialog,
  onOpenGenerateTestClaims,
  onPrint,
  onExportCsv,
}) => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Claims
        </h1>
        <p className="text-slate-500 mt-1">
          {totalCount} total claims • {draftCount} drafts
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        {selectedCount > 0 && (
          <>
            <Button
              onClick={onBatchSubmit}
              variant="outline"
              className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid="batch-submit-btn"
              title="Mark selected Draft claims as Pending (no live submission)"
            >
              <PaperPlaneTilt size={18} className="mr-2" />
              Mark {selectedCount} Pending
            </Button>
            <Button
              onClick={onOpenOdmDialog}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="send-to-odm-btn"
              title="Generate 837P EDI and upload to Ohio Medicaid SFTP — claims become Submitted"
            >
              <PaperPlaneTilt size={18} className="mr-2" weight="fill" />
              Send {selectedCount} to ODM
            </Button>
          </>
        )}
        <Button
          onClick={onPrint}
          variant="outline"
          className="border-slate-200 text-slate-700"
          data-testid="print-claims-btn"
          title="Open the browser print dialog with the current filtered table"
        >
          <Printer size={18} className="mr-2" /> Print
        </Button>
        <Button
          onClick={onExportCsv}
          variant="outline"
          className="border-slate-200 text-slate-700"
          data-testid="download-claims-csv-btn"
          title="Download the current filtered list as CSV"
        >
          <DownloadSimple size={18} className="mr-2" /> Download List
        </Button>
        <Button
          onClick={onOpenGenerateTestClaims}
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="generate-test-claims-btn"
          title="Build a batch of Draft test claims (one per eligible patient) for ODM CERT testing"
        >
          <Flask size={18} className="mr-2" />
          Generate Test Claims
        </Button>
        <Button
          onClick={() => navigate('/claims/new')}
          className="bg-indigo-900 hover:bg-indigo-800"
          data-testid="new-claim-btn"
        >
          <Plus size={18} className="mr-2" />
          New Claim
        </Button>
      </div>
    </div>
  );
};

export default ClaimsHeader;
